import { Component, Input, OnInit } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { BaseComponent } from "app/base/base.component";
import { MappingEntry } from "app/core/models/mappingEntry";
import { MappingTable } from "app/core/models/mappingTable";
import { PointOfService } from "app/core/models/pointOfService";
import { MappingTableService } from "app/core/services/mapping-table.service";
import { NotifMessageService } from "app/core/services/notifmessage.service";
import { PosService } from "app/core/services/pos.service";
import { AuthenticatedRouteGuard } from "security/authenticatedRouteGuard.service";

@Component({
  selector: "app-mapping-tables",
  templateUrl: "./mapping-tables.component.html",
})
export class MappingTablesComponent extends BaseComponent implements OnInit {
  @Input() merchantId : number;
  @Input() isEditMode: boolean;
  @Input() isDetailsMode: boolean;

  mappingTables : Array<MappingTable> = new Array<MappingTable>();

  isCreatingNewTable: boolean = false;

  mappingEntryForm: FormGroup;
  mappingTableForm: FormGroup;

  lastTerm: String;
  posList: Array<PointOfService> = new Array<PointOfService>();
  selectedMappingTable: MappingTable = new MappingTable();

  mappingTableControl: FormControl = new FormControl();

  pager = {
    page: 0,
    size: 20,
    sort: "externalRef,ASC",
  };

  constructor(
    private mappingTableService: MappingTableService,
    private roleGuard: AuthenticatedRouteGuard,
    private notifService: NotifMessageService,
    private formBuilder: FormBuilder,
    private translateService: TranslateService,
    private posService: PosService,
    public router: Router,
  ) {super(router);}

  ngOnInit(): void {    
    if (this.isEditMode) {
      this.initForm();
    }
    if (this.roleGuard.isEnabled("MAPPING_TABLE_READ") && this.roleGuard.isEnabled("MAPPING_TABLE_EDIT") && this.merchantId !== undefined){
      this.getMappingTables(this.merchantId);
    }
  }

  get mappingEntry() {
    return this.mappingEntryForm.controls;
  }

  get mappingTable() {
    return this.mappingTableForm.controls;
  }

  initForm() : void {
    this.mappingEntryForm = this.formBuilder.group({
      sourceProductCode: new FormControl('', Validators.required),
      targetProductCode: new FormControl('', Validators.required),
      productDescription: new FormControl('', Validators.required)
    });

    this.mappingTableForm = this.formBuilder.group({
      tableName: new FormControl('', Validators.required),
      posExternalRef: new FormControl('', Validators.required)
    });
  }

  getMappingTables(merchantId, name = "DEFAULT_TABLE") {
    this.mappingTableService.getMappingTableByMerchantId(merchantId).subscribe((mappingTables) => {
      this.mappingTables = mappingTables;
      if (this.isEditMode && this.mappingTables.length == 0) {
        this.isCreatingNewTable = true;
        this.mappingTableForm.get("tableName").setValue
        ("DEFAULT_TABLE");
        this.mappingTableForm.get("posExternalRef").setValue("_default_");
        this.mappingTableForm.disable();
        this.mappingTableForm.updateValueAndValidity();
        return;
      } else if (this.isEditMode) {
        this.mappingTableForm.enable();
        this.mappingTableForm.updateValueAndValidity();
      } else if (this.mappingTables.length == 0) {
        return;
      }
      this.selectedMappingTable = this.mappingTables.find(
        (table) => table.name == name
      );

      if (this.selectedMappingTable == undefined) {
        this.selectedMappingTable = this.mappingTables[0];
      }

      this.mappingTableControl = new FormControl(
        this.selectedMappingTable.id
      );
      this.mappingTableManagement();
      this.isCreatingNewTable = false;
    });
  }

  addMappingEntry() {
    let newMappingEntry: MappingEntry = new MappingEntry();
    newMappingEntry.sourceProductCode =
      this.mappingEntryForm.get("sourceProductCode").value;
    newMappingEntry.targetProductCode =
      this.mappingEntryForm.get("targetProductCode").value;
    newMappingEntry.productDescription =
      this.mappingEntryForm.get("productDescription").value;
    newMappingEntry.merchantId = this.merchantId;

    this.selectedMappingTable.mappingEntries.push(newMappingEntry);

    this.mappingTableService
      .updateMappingTable(this.selectedMappingTable)
      .subscribe((res) => {
        if (res != null) {
          this.mappingEntryForm.reset();
          this.notifService.notifySuccess("MAPPING_TABLE_UPDATED");
        }
        this.getMappingTables(this.merchantId, this.selectedMappingTable.name);
      });
  }

  removeMappingEntry(id: String) {
    const index = this.selectedMappingTable.mappingEntries.findIndex(
      (entry) => entry.id == id
    );
    this.selectedMappingTable.mappingEntries.splice(index, 1);

    this.mappingTableService
      .updateMappingTable(this.selectedMappingTable)
      .subscribe((res) => {
        if (res != null) {
          this.notifService.notifySuccess("MAPPING_TABLE_UPDATED");
        }
        this.getMappingTables(this.merchantId, this.selectedMappingTable.name);
      });
  }

  clickToCreateNewTable() {
    this.isCreatingNewTable = true;
    this.mappingTableForm.reset();
  }

  cancelCreateNewTable() {
    this.isCreatingNewTable = false;
  }

  createNewTable() {
    let newMappingTable: MappingTable = new MappingTable();

    newMappingTable.merchantId = this.merchantId;
    newMappingTable.name = this.mappingTableForm.get("tableName").value;
    newMappingTable.posExternalRef =
      this.mappingTableForm.get("posExternalRef").value;

    this.mappingTableService
      .createMappingTable(newMappingTable)
      .subscribe((res) => {
        if (res != null) {
          this.notifService.notifySuccess("MAPPING_TABLE_CREATED");
          this.getMappingTables(this.merchantId, newMappingTable.name);
        } else {
          this.getMappingTables(this.merchantId);
        }
      });
  }

  mappingTableManagement(): void {
    this.mappingTableControl.valueChanges.subscribe((val) => {
      this.selectedMappingTable = this.mappingTables.find(
        (table) => table.id == val
      );
    });
  }

  nameAndposExternalRefSearchFn = (term: string, item: any) => {
    term = term.toLocaleLowerCase();
    return this.translateService.instant(item.name).toLocaleLowerCase().indexOf(term) > -1 || item.posExternalRef.toLocaleLowerCase().indexOf(term) > -1;
  }

  posByExternalRefSearchFn = (term: string, item: any) => {
    return item.externalRef.indexOf(term) > -1;
  }

  onPosSearch = (event) => {
    if (event.term && this.lastTerm != event.term && event.term.length > 2) {
      this.lastTerm = event.term;
      this.posService.findByCriteria({ externalRef: event.term }, this.pager, false).subscribe(poses => {
        this.posList = poses.content;
      });
    }
  }
}
